import { useState } from 'react';
import { teamFilter } from '../use-teams';
import { type LoggingView } from '@forecastify/shared';
import { AvatarFace, Button, Icon, ListToolbar, Tooltip, LandscapeOnly } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useI18n, useT, type Translate } from '../../i18n';
import { identityColor, initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { RangeControl } from './range-control';
import { PeopleGridSkeleton } from './grid-skeleton';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ LOGOLÁS — the mockup's `dashlog`, a spreadsheet with three lines.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The first version was a strip of coloured squares, one per day, with a variance at the end and no
 * figures at all. The product owner's report: *"no numbers, no detailed view breakdown, no search,
 * no toggle between monthly and daily"*. This is the mockup, transcribed:
 *
 * | Thing              | Value                                             |
 * | ------------------ | ------------------------------------------------- |
 * | person column      | 240px, sticky left                                |
 * | line-label column  | 104px, sticky at 240px                            |
 * | a span column      | 104px                                             |
 * | a line             | 32px (`h-8`), the project rows 44px (`h-11`)       |
 * | header             | sticky top, `bg-muted`, the current span tinted   |
 *
 * ⚠️⚠️ **TWO STICKY COLUMNS, AND THE SECOND ONE IS WHY THIS READS AT ALL.** Scrolling a quarter
 * sideways with only the name pinned leaves three unlabelled rows per person; the mockup pins the
 * line labels too, so *Munkaidő / Rögzített / Szabadság* stay beside their figures.
 *
 * ⚠️ **Every figure comes from the server**, spans and verdicts included — `dashboards.service.ts`
 * folds C44's kernel into the same columns this draws, so a cell and the day screen behind it
 * cannot disagree.
 */

/**
 * The mockup's `DAY_KIND` colours, and the tint it lays behind a cell.
 *
 * ⚠️⚠️ **`holiday` JOINED THEM ON 15 Sep**, and it is the only one here that is not a judgement
 * about somebody's logging: it says *nothing was expected of anybody today*. Reported as a
 * workspace-wide day off reading as an ordinary empty cell — which, in a grid whose empty cells
 * mean *nobody logged*, is an accusation.
 */
const VERDICT_COLOR: Readonly<Record<string, string>> = {
  under: 'var(--status-bad)',
  over: 'var(--log-over)',
  ok: 'var(--status-ok)',
  holiday: 'var(--leave)',
};

/**
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ LEAVE IS YELLOW HERE TOO — round 5 item 6.                                │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * *"Logolás dashboardnál a szabadság cella nem sárga, az erőforrásnál igen, legyen itt is sárga
 * mint az erőforrásnál."* The two grids sit one click apart and a person away from their desk is
 * the same fact on both; painting it on one screen and not the other makes the reader check twice.
 *
 * ⚠️ **The same two tokens `resource-screen.tsx` uses** — `--leave` tinted for the fill and
 * `--leave-ink` for the figure, which is the amber that reads on the tint rather than the tint's
 * own colour at full strength.
 */
const LEAVE_FILL = 'var(--leave)';
const LEAVE_INK = 'var(--leave-ink)';

function tint(color: string | undefined): string | undefined {
  /* ⚠️ 12% of the verdict's own colour — the mockup's `tint(col, 12)`. A solid fill would make the
     grid unreadable; a border would move the figures. */
  return color === undefined ? undefined : `color-mix(in oklch, ${color} 12%, transparent)`;
}

export function LoggingScreen(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /**
   * The period requested — shown by the control at once, where `view.from`/`to` arrive only with
   * the answer (14 Oct 2026: UAT read *Időszak* for seven seconds). Absent, the view's own span.
   */
  readonly range?: { readonly from: string; readonly to: string };
  readonly view: LoggingView;
  readonly managers: readonly { readonly id: string; readonly name: string }[];
  readonly projects: readonly { readonly id: string; readonly name: string }[];
  readonly managerId: string;
  readonly projectId: string;
  readonly scheduleType: string;
  readonly varianceOnly: boolean;
  readonly query: string;
  readonly onManager: (id: string) => void;
  /** R-60 · the team filter — absent where the screen is embedded without one. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (id: string) => void;
  readonly onProject: (id: string) => void;
  readonly onScheduleType: (value: string) => void;
  readonly onVarianceOnly: (value: boolean) => void;
  readonly onQuery: (value: string) => void;
  readonly onMode: (mode: 'week' | 'day') => void;
  /** What the picker's *Alapértelmezett* preset goes back to — the screen's own opening window. */
  readonly defaultRange: { readonly from: string; readonly to: string };
  readonly onRange: (from: string, to: string) => void;
  readonly onOpenPerson: (memberId: string) => void;
  /** 28 Oct 2026 · the red mark: their page, with the time logs on *Ismeretlen sortételek*. */
  readonly onOpenUnknown?: (memberId: string) => void;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const { locale } = useI18n();
  const [open, setOpen] = useState<readonly string[]>([]);

  /* ⚠️ One fetch per distinct picture for the whole app — `use-avatar.ts` argues why this cannot
     be a hook per row. The disc falls back to initials on the person's identity colour. */
  const avatars = useAvatars(props.view.rows.map((row) => row.avatarUrl));
  /* ⚠️ The project leads' pictures, from the same cache — one flat list over every expanded row,
     because the hook cannot be called per row (`use-avatar.ts`). */
  const pmAvatars = useAvatars(
    props.view.rows.flatMap((row) => row.projects.map((project) => project.pmAvatarUrl)),
  );

  const toggle = (memberId: string): void => {
    setOpen((current) =>
      current.includes(memberId)
        ? current.filter((one) => one !== memberId)
        : [...current, memberId],
    );
  };

  /* ⚠️ The weekday's NAME is language, so the wire carries 1–7 and `Intl` says the word — the same
     rule `schedules-section.tsx` follows for its seven columns. */
  const weekdayName = (index: string): string =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(
      new Date(Date.UTC(2024, 0, Number(index))),
    );

  const columnCount = props.view.columns.length;

  return (
    /* 17 Oct 2026 · a people × days grid reads only sideways on a phone — the product owner. */
    <LandscapeOnly message={t('mobile.rotate')}>
      <div data-screen="dash-logging" className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b pr-3">
          <ListToolbar
            className="flex-1 border-b-0"
            labels={{
              search: t('dash.search'),
              filter: t('list.filter'),
              views: t('list.views'),
              columns: t('list.columns'),
              clearAll: t('list.clearAll'),
              searchOptions: t('list.searchOptions'),
              clearFilter: (label: string) => t('list.clearFilter', { label }),
              rangeFrom: t('list.rangeFrom'),
              rangeTo: t('list.rangeTo'),
              switchView: t('list.switchView'),
              more: t('list.more'),
              back: t('list.back'),
              close: t('common.close'),
              selectAll: t('list.selectAll'),
              clearSelection: t('list.clearSelection'),
            }}
            search={props.query}
            onSearchChange={props.onQuery}
            views={[
              { value: 'week', label: t('dash.view.week') },
              { value: 'day', label: t('dash.view.day') },
            ]}
            view={props.view.mode}
            onViewChange={(value) => {
              props.onMode(value === 'day' ? 'day' : 'week');
            }}
            filters={[
              {
                key: 'manager',
                /* 3 Nov 2026 · several at once, OR — nothing ticked is everybody. */
                kind: 'multi' as const,
                label: t('dash.filter.manager'),
                value: props.managerId,
                onChange: props.onManager,
                options: props.managers.map((one) => ({ value: one.id, label: one.name })),
              },
              ...(props.onTeam === undefined
                ? []
                : teamFilter(t, props.teams ?? [], props.teamId ?? '', props.onTeam)),
              {
                key: 'project',
                kind: 'multi' as const,
                label: t('dash.filter.project'),
                value: props.projectId,
                onChange: props.onProject,
                options: props.projects.map((one) => ({ value: one.id, label: one.name })),
              },
              {
                key: 'schedule',
                /* 3 Nov 2026 · *Nincs beosztás* is an ordinary option of the multi (R-62, amended) —
                   `none,fix` is "no schedule or a fixed one", ORed on the server. */
                kind: 'multi' as const,
                label: t('dash.filter.schedule'),
                value: props.scheduleType,
                onChange: props.onScheduleType,
                options: [
                  { value: 'fix', label: t('settings.schedules.type.fix') },
                  { value: 'flex', label: t('settings.schedules.type.flex') },
                  { value: 'none', label: t('people.field.noSchedule') },
                ],
              },
              {
                key: 'variance',
                label: t('dash.column.variance'),
                value: props.varianceOnly ? 'diff' : '',
                onChange: (value) => {
                  props.onVarianceOnly(value === 'diff');
                },
                options: [
                  { value: '', label: t('dash.filter.allSchedules') },
                  { value: 'diff', label: t('dash.filter.varianceOnly') },
                ],
              },
            ]}
            /* 17 Oct 2026 · the period rides the toolbar, so a phone places it beside the search. */
            trailing={
              <RangeControl
                /* ⚠️ The period ASKED for, so the control names it before the answer lands. */
                value={props.range ?? { from: props.view.from, to: props.view.to }}
                defaultRange={props.defaultRange}
                /* ⚠️⚠️ **ONE COLUMN PER PRESS — round 5 item 6.** A week in the weekly view and a
             day in the daily one, rather than the whole selected window: with sixteen weeks on
             screen, a full-window step replaces every column at once and leaves nothing in
             common between the two views. `range-control.tsx` argues it at the prop. */
                stepDays={props.view.mode === 'day' ? 1 : 7}
                onChange={(range) => {
                  props.onRange(range.from, range.to);
                }}
              />
            }
          />
        </div>

        {props.loading === true ? (
          <PeopleGridSkeleton />
        ) : props.view.rows.length === 0 ? (
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
            <Icon name="users" size={22} className="opacity-60" />
            <span className="text-[13px]">{t('dash.empty.logging')}</span>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <div className="min-w-max">
              {/* ── the header ─────────────────────────────────────────────────────────────── */}
              <div className="bg-muted sticky top-0 z-20 flex border-b">
                <div className="bg-muted text-muted-foreground sticky left-0 z-30 flex w-[240px] shrink-0 items-center border-r px-3 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
                  {t('dash.column.person')}
                </div>
                <div className="bg-muted sticky left-[240px] z-30 w-[104px] shrink-0 border-r" />
                {props.view.columns.map((column) => (
                  <div
                    key={column.key}
                    className={`flex w-[104px] shrink-0 flex-col justify-center gap-0.5 border-l px-2 py-1.5 ${
                      column.current ? 'bg-primary/10' : ''
                    }`}
                  >
                    <span className="text-[11px] font-medium tabular-nums">{column.top}</span>
                    <span className="text-muted-foreground text-[10.5px] font-medium tracking-wide uppercase">
                      {props.view.mode === 'day' ? weekdayName(column.sub) : column.sub}
                    </span>
                  </div>
                ))}
              </div>

              {/* ── one block per person ───────────────────────────────────────────────────── */}
              {props.view.rows.map((row) => {
                const expanded = open.includes(row.memberId);
                const avatar = avatars.get(row.avatarUrl ?? '');
                /* ⚠️ The frame where there is one — P3-7's flex people — else the project role, else
                 nothing at all. */
                const subtitle =
                  row.weeklyFrame === null
                    ? row.role
                    : t('dash.row.frame', { hours: fmt(Number(row.weeklyFrame)) });
                const lines: {
                  label: string;
                  cells: LoggingView['rows'][number]['logged'];
                  leave?: boolean;
                }[] = [
                  { label: t('dash.line.planned'), cells: row.planned },
                  { label: t('dash.line.logged'), cells: row.logged },
                  /* ⚠️ `leave` marks the row so its cells can take the amber — matching on the
                   translated LABEL would break the moment somebody switched language. */
                  { label: t('dash.line.leave'), cells: row.leave, leave: true },
                ];

                return (
                  /* ⚠️ The hover is on the BLOCK, not the row: three lines belong to one person and
                   highlighting one of them would cut the person in three. */
                  <div key={row.memberId} className="hover:bg-accent/15 border-b transition-colors">
                    <div className="flex">
                      <div className="bg-background sticky left-0 z-10 flex w-[240px] shrink-0 items-start gap-2 border-r px-2 py-2">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('dash.row.breakdown')}
                          onClick={() => {
                            toggle(row.memberId);
                          }}
                        >
                          <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
                        </Button>
                        <button
                          type="button"
                          className="flex min-w-0 items-center gap-2 text-left"
                          onClick={() => {
                            props.onOpenPerson(row.memberId);
                          }}
                        >
                          {avatar === undefined ? (
                            <span
                              aria-hidden
                              className="text-on-id grid size-6 shrink-0 place-items-center rounded-full text-[9.5px] font-semibold"
                              style={{ background: identityColor(row.memberId) }}
                            >
                              <AvatarFace
                                seed={row.avatarSeed ?? undefined}
                                fallback={initialsOf(row.memberName)}
                                size={24}
                              />
                            </span>
                          ) : (
                            <img
                              src={avatar}
                              alt=""
                              className="size-6 shrink-0 rounded-full object-cover"
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1">
                              <span className="truncate text-[12.5px] font-medium">
                                {row.memberName}
                              </span>
                            </span>
                            {/**
                             * ⚠️⚠️ **NO LINE RATHER THAN AN EM-DASH — 15 Sep.** A second line reading
                             * `—` under every name without a project role was a column of dashes that
                             * said nothing and cost a line of height on every row. An em-dash earns its
                             * place in a TABLE cell, where a blank cannot be told from data that did
                             * not arrive (`state-rules.md` §5); under a name it is just noise.
                             */}
                            {subtitle === null ? null : (
                              <span className="text-muted-foreground block truncate text-[11px]">
                                {subtitle}
                              </span>
                            )}
                          </span>
                        </button>
                        {/* ⚠️⚠️ 28 Oct 2026 · OUTSIDE the name's button, because it is a button
                            itself: it opens their page with the time logs filtered to the hours
                            it counts. A button inside a button is not one a keyboard can reach. */}
                        {/* ⚠️⚠️ 28 Oct 2026 · TWO MARKS, AND THEY ARE NOT EQUAL. Hours on an unknown
                            line item bill nobody until somebody routes them — a red warning, and a
                            button: it opens their page with the time logs filtered to those hours.
                            Outside the name's button, because a button inside a button is one no
                            keyboard can reach. Short days are a note: the grid's own colours
                            already say which week fell short. */}
                        <span className="flex shrink-0 items-center gap-1">
                          {row.unknownHours === null ? null : (
                            <Tooltip
                              content={t('dash.row.unknownHours', { hours: fmt(row.unknownHours) })}
                            >
                              <button
                                type="button"
                                aria-label={t('dash.row.openUnknown', {
                                  hours: fmt(row.unknownHours),
                                })}
                                className="hover:border-status-bad-text grid size-6 shrink-0 cursor-pointer place-items-center rounded border border-transparent transition-colors"
                                onClick={() => {
                                  (props.onOpenUnknown ?? props.onOpenPerson)(row.memberId);
                                }}
                              >
                                {/* 30 Oct 2026 · the sidebar's marks: a red question mark here,
                                    an amber alert for short days. */}
                                <Icon
                                  name="circle-help"
                                  size={13}
                                  className="text-status-bad-text"
                                />
                              </button>
                            </Tooltip>
                          )}
                          {row.shortDays === 0 ? null : (
                            <Tooltip content={t('dash.row.shortDays', { count: row.shortDays })}>
                              <span
                                role="img"
                                aria-label={t('dash.row.shortDays', { count: row.shortDays })}
                                className="flex items-center"
                              >
                                <Icon name="circle-alert" size={13} className="text-status-warn" />
                              </span>
                            </Tooltip>
                          )}
                        </span>
                        <span className="flex-1" />
                      </div>

                      <div className="bg-background sticky left-[240px] z-10 w-[104px] shrink-0 border-r">
                        {lines.map((line) => (
                          <div
                            key={line.label}
                            className="text-muted-foreground flex h-8 items-center justify-end px-2 text-[11.5px] font-medium"
                          >
                            {line.label}
                          </div>
                        ))}
                      </div>

                      <div>
                        {lines.map((line) => (
                          <div key={line.label} className="flex">
                            {line.cells.map((cell, index) => {
                              const color =
                                cell.verdict === null ? undefined : VERDICT_COLOR[cell.verdict];
                              /* ⚠️ Only where somebody is ACTUALLY away: an ordinary week would
                               otherwise carry a yellow column of zeroes saying nothing happened.
                               ⚠️ 3 Nov 2026 · the server sends HOURS as a number, and `null` for
                               no leave (it was `''` while the cell carried `H:MM` text). */
                              const onLeave = line.leave === true && cell.value !== null;
                              /* A leave line with none stays blank, as it did; a plan with none is
                                 the em-dash below. DU-1 · the figure in the reader's unit. */
                              const text =
                                cell.value === null
                                  ? line.leave === true
                                    ? ''
                                    : null
                                  : fmt(cell.value);
                              return (
                                <Tooltip
                                  key={props.view.columns[index]?.key ?? String(index)}
                                  content={cellTitle(t, line.label, text)}
                                  asChild
                                >
                                  <div
                                    className={`flex h-8 w-[104px] shrink-0 items-center justify-end border-l px-2 text-[12px] tabular-nums ${
                                      cell.value === null ? 'text-muted-foreground/60' : ''
                                    }`}
                                    style={
                                      onLeave
                                        ? { background: tint(LEAVE_FILL), color: LEAVE_INK }
                                        : { background: tint(color), color }
                                    }
                                  >
                                    {/* ⚠️ An em-dash where there is no plan — P3-7: a flex person is
                                      measured weekly, and a zero would claim nothing was expected. */}
                                    {text ?? '–'}
                                  </div>
                                </Tooltip>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── the project breakdown ─────────────────────────────────────────────── */}
                    {!expanded ? null : row.projects.length === 0 ? (
                      <div className="text-muted-foreground bg-muted/25 border-t px-3 py-2 pl-10 text-[11.5px]">
                        {t('dash.row.noLogs')}
                      </div>
                    ) : (
                      row.projects.map((project) => (
                        <div
                          key={project.projectId ?? project.kind}
                          className="bg-muted/25 flex border-t"
                        >
                          <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-0 z-10 flex h-11 w-[240px] shrink-0 items-center gap-2 border-r pr-2 pl-10">
                            <span
                              aria-hidden
                              className="size-2 shrink-0 rounded-[3px]"
                              style={{
                                background:
                                  project.kind === 'unknown'
                                    ? 'var(--status-bad)'
                                    : (project.color ?? 'var(--muted-foreground)'),
                              }}
                            />
                            <span className="min-w-0 flex-1">
                              {/* ⚠️ `markup.html` 3220 — a breakdown sub-row's name is weighted. */}
                              <span className="block truncate text-[12px] font-medium">
                                {/* 28 Oct 2026 · pinned first by the server; its hours are out
                                    of the project lines below. */}
                                {project.kind === 'unknown'
                                  ? t('dash.row.unknownLineItem')
                                  : project.name}
                              </span>
                              {project.pmName === null ? null : (
                                /* ⚠️ The lead as a FACE — 15 Sep. The row was given the height for
                                 one and then kept a line of grey text in it. */
                                <span className="text-muted-foreground mt-0.5 flex items-center gap-1 truncate text-[10.5px]">
                                  {pmAvatars.get(project.pmAvatarUrl ?? '') === undefined ? (
                                    <span
                                      aria-hidden
                                      className="text-on-id grid size-4 shrink-0 place-items-center rounded-full text-[7px] font-semibold"
                                      style={{
                                        background: identityColor(
                                          project.pmMemberId ?? project.projectId ?? 'pm',
                                        ),
                                      }}
                                    >
                                      <AvatarFace
                                        seed={project.pmAvatarSeed ?? undefined}
                                        fallback={initialsOf(project.pmName)}
                                        size={16}
                                      />
                                    </span>
                                  ) : (
                                    <img
                                      src={pmAvatars.get(project.pmAvatarUrl ?? '')}
                                      alt=""
                                      className="size-4 shrink-0 rounded-full object-cover"
                                    />
                                  )}
                                  <span className="truncate">{project.pmName}</span>
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-[240px] z-10 flex h-11 w-[104px] shrink-0 items-center justify-end border-r px-2 text-[11px]">
                            {t('dash.line.logged')}
                          </div>
                          <div className="flex">
                            {project.hours.map((value, index) => (
                              <div
                                key={props.view.columns[index]?.key ?? String(index)}
                                className={`flex h-11 w-[104px] shrink-0 items-center justify-end border-l px-2 text-[11.5px] tabular-nums ${
                                  value === 0 ? 'text-muted-foreground/45' : ''
                                }`}
                              >
                                {fmt(value)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ⚠️ No figure while loading — `0 people` is a claim about the team. */}
        <div className="text-muted-foreground flex min-h-[33px] items-center gap-3 border-t px-4 py-2 text-[11.5px]">
          {props.loading === true ? null : (
            <>
              <span>{t('dash.footer.people', { count: props.view.rows.length })}</span>
              <span>{t('dash.footer.columns', { count: columnCount })}</span>
            </>
          )}
        </div>
      </div>
    </LandscapeOnly>
  );
}

function cellTitle(t: Translate, line: string, value: string | null): string {
  return value === null ? t('dash.verdict.free') : `${line} · ${value}`;
}
